/**
 * Contract tags — FR-02, NFR-06, NFR-07. R-77-1 (Gerry, 11 Sep 2026) · CLM-77.
 *
 * *"the user should be able to define tags on the contracts so they can segment
 * and search by tags like finance contracts or manufacturing contracts."* Until
 * CLM-77 the only segmentation axis was `category`; tags are free-form, many per
 * contract, and a **filter** (a predicate on the row's own tags — FR-20/FR-27: a
 * tag filter narrows, it never re-ranks).
 *
 * ## One normaliser, two callers (D-77-1)
 *
 * A tag is stored normalised: lowercase, trimmed, internal whitespace collapsed
 * to one space, hyphens kept as typed, charset `[a-z0-9 -]`, 1..40 characters, at
 * most 20 per contract. `normaliseTag` is the only place that rule is written —
 * the web `TagInput` previews with it and the API re-normalises with it and 400s
 * on anything the client sent unnormalised (the client's casing is never
 * trusted, the `execute-sharepoint-ingest` rule). `TagSchema`'s regex and the
 * `ck_contract_tag_shape` CHECK in migration 0089 are byte-identical apart from
 * the non-capturing group, which Postgres ARE lacks.
 *
 * `north-east` and `north east` are distinct tags; suggestions steer users to the
 * one already in scope.
 *
 * ## Two endpoints
 *
 *  - `manage-contract-tags` — POST, the body is the **replacement set** (D-77-5,
 *    last write wins). One attributed transaction: DELETE the tags not in the
 *    set, INSERT the ones missing, and write exactly ONE `Update` audit row with
 *    `field_name 'tags'` and both sets as objects — the `contract_tag` table
 *    deliberately carries no trigger (D-77-4), so a set of five tags is one
 *    governance write, not five (rule 7). A no-op set still writes the row: the
 *    request was a governance write and the evidence chain records it. Gate
 *    `contract:update` (contributor+, D-77-2). 404 when the contract is outside
 *    the caller's scope or absent — retrieve-contract's behaviour.
 *  - `contract-tags-suggest` — GET, the tags already in the caller's scope that
 *    start with `q`, most-used first. Scoped: a practice never learns another's
 *    vocabulary. Gate `contract:read` — readers need the list to *filter*.
 */
import { z } from 'zod';
import { ContractKeySchema } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
export const TAG_MAX_LENGTH = 40;
export const TAGS_PER_CONTRACT_MAX = 20;
/**
 * The stored shape. Mirrors `ck_contract_tag_shape` (0089):
 * `'^[a-z0-9]+([ -][a-z0-9]+)*$'` — capturing there, non-capturing here.
 */
export const TAG_PATTERN = /^[a-z0-9]+(?:[ -][a-z0-9]+)*$/;
export const TagSchema = z
    .string()
    .min(1)
    .max(TAG_MAX_LENGTH)
    .regex(TAG_PATTERN, 'expected a normalised tag: a-z, 0-9, single spaces or hyphens');
/**
 * Lowercase → trim → internal whitespace to one space. `null` when nothing is
 * left, when a character outside `[a-z0-9 -]` survives, when a hyphen is not
 * between two words, or when the result is longer than `TAG_MAX_LENGTH`.
 *
 *   `"  Finance   Contracts "` → `finance contracts`
 *   `"Oil&Gas"`                → `null` (charset)
 *   `"north-east"`             → `north-east`
 *   `"a".repeat(41)`           → `null` (length)
 */
export function normaliseTag(raw) {
    const collapsed = raw.toLowerCase().trim().replace(/\s+/g, ' ');
    if (collapsed.length === 0 || collapsed.length > TAG_MAX_LENGTH)
        return null;
    return TAG_PATTERN.test(collapsed) ? collapsed : null;
}
/**
 * Normalise a list: each entry through `normaliseTag`, duplicates folded (stable
 * order, first occurrence wins), and everything past the `max`-th distinct tag
 * `rejected`.
 */
export function normaliseTags(raw, max = TAGS_PER_CONTRACT_MAX) {
    const tags = [];
    const rejected = [];
    const seen = new Set();
    for (const entry of raw) {
        const tag = normaliseTag(entry);
        if (tag === null) {
            rejected.push(entry);
            continue;
        }
        if (seen.has(tag))
            continue;
        if (tags.length >= max) {
            rejected.push(entry);
            continue;
        }
        seen.add(tag);
        tags.push(tag);
    }
    return { tags, rejected };
}
/** `true` when every entry is already normalised and there are no duplicates — what the API demands of a body. */
export function tagsAreNormalised(tags) {
    const seen = new Set();
    for (const tag of tags) {
        if (normaliseTag(tag) !== tag || seen.has(tag))
            return false;
        seen.add(tag);
    }
    return true;
}
/** Filter-mode shape shared by search, the notification list, the verification queue and the pipeline status: all-of, 1..20. */
export const TagFilterSchema = z.array(TagSchema).min(1).max(TAGS_PER_CONTRACT_MAX);
/* -------------------------------------------------------------------------- */
/* manage-contract-tags                                                        */
/* -------------------------------------------------------------------------- */
export const ManageContractTagsRequestSchema = z
    .object({
    contract_key: ContractKeySchema,
    /** The replacement set (D-77-5). Every entry normalised; the API 400s otherwise. */
    tags: z.array(TagSchema).max(TAGS_PER_CONTRACT_MAX),
})
    .strict();
export const ManageContractTagsResponseSchema = z.object({
    contract_key: ContractKeySchema,
    /** The stored set after the call, sorted. */
    tags: z.array(TagSchema),
    added: z.array(TagSchema),
    removed: z.array(TagSchema),
    /** The one `Update` row (`field_name 'tags'`) this call wrote — rule 7. */
    audit: AuditReceiptSchema,
});
/* -------------------------------------------------------------------------- */
/* contract-tags-suggest                                                       */
/* -------------------------------------------------------------------------- */
export const ContractTagSuggestRequestSchema = z
    .object({
    /** Prefix, normalised server-side before matching; omit for the most-used tags in scope. */
    q: z.string().max(TAG_MAX_LENGTH).optional(),
    limit: z.number().int().min(1).max(50).optional(),
})
    .strict();
export const ContractTagSuggestionSchema = z.object({
    tag: TagSchema,
    /** Contracts in the caller's scope carrying the tag. */
    contracts: z.number().int().min(0),
});
export const ContractTagSuggestResponseSchema = z.object({
    /** Most-used first, then alphabetical. */
    suggestions: z.array(ContractTagSuggestionSchema),
});
//# sourceMappingURL=contract-tags.js.map